import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Plane, Ship, ChevronLeft, ChevronRight, CalendarDays, MapPin, Store, Warehouse, Phone, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  scheduled: { label: 'Planifié', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  planned: { label: 'Planifié', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  in_transit: { label: 'En transit', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  in_progress: { label: 'En transit', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  delayed: { label: 'Retardé', cls: 'bg-red-100 text-red-700 border-red-200' },
  arrived: { label: 'Arrivé', cls: 'bg-green-100 text-green-700 border-green-200' },
  completed: { label: 'Terminé', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
};
const statusMeta = (s) => STATUS_META[s] || { label: s || '—', cls: 'bg-muted text-muted-foreground border-border' };

// Couleur propre pour planifié (bleu) et en cours (ambre) ; les autres statuts
// (arrivé, terminé, retardé…) sont grisés.
const FEATURED_STATUSES = new Set(['scheduled', 'planned', 'in_transit', 'in_progress']);
const isFeatured = (s) => FEATURED_STATUSES.has(s);

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

const DepartureCard = ({ dep }) => {
  const meta = statusMeta(dep.status);
  const departure = fmtDate(dep.departure_date);
  const arrival = fmtDate(dep.expected_arrival_date);  // arrivée estimée
  const deadline = fmtDate(dep.deposit_deadline);
  const featured = isFeatured(dep.status);
  return (
    <div className={`rounded-lg border p-3 text-left ${featured ? 'border-border bg-card' : 'border-border/60 bg-muted/30 opacity-70'}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-sm text-foreground truncate">{dep.name}</span>
        <span className={`shrink-0 text-[11px] px-2 py-0.5 rounded-full border ${meta.cls}`}>{meta.label}</span>
      </div>
      <div className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
        {dep.transport_mode === 'maritime' ? <Ship size={13} /> : <Plane size={13} />}
        <MapPin size={12} />
        <span className="truncate">{dep.origin_country} → {dep.destination_country}</span>
      </div>
      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
        {dep.flight_name && <span>Vol : <span className="font-medium text-foreground">{dep.flight_name}</span></span>}
        {departure && <span>Départ : <span className="font-medium text-foreground">{departure.toLocaleDateString('fr-FR')}</span></span>}
        {arrival && <span>Arrivée estimée : {arrival.toLocaleDateString('fr-FR')}</span>}
      </div>
      {deadline && (
        <div className="mt-1.5 flex items-center gap-1 text-[11px] text-muted-foreground/60">
          <Clock size={11} />
          Dépôt colis avant le {deadline.toLocaleDateString('fr-FR')}{deadline.getHours() || deadline.getMinutes() ? ` à ${deadline.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}` : ''}
        </div>
      )}
    </div>
  );
};

const PlaceCard = ({ place, icon: Icon }) => (
  <div className="rounded-lg border border-border bg-card p-3 text-left">
    <div className="flex items-center gap-2">
      <Icon size={15} className="text-orange-500 shrink-0" />
      <span className="font-semibold text-sm text-foreground truncate">{place.name || '—'}</span>
    </div>
    <div className="mt-1.5 space-y-0.5 text-xs text-muted-foreground">
      {(place.address || place.city) && (
        <div className="flex items-start gap-1.5">
          <MapPin size={12} className="mt-0.5 shrink-0" />
          <span>{[place.address, place.city, place.country].filter(Boolean).join(', ')}</span>
        </div>
      )}
      {place.phone && <div className="flex items-center gap-1.5"><Phone size={12} /> {place.phone}</div>}
      {place.opening_hours && <div className="flex items-center gap-1.5"><Clock size={12} /> {place.opening_hours}</div>}
    </div>
  </div>
);

const PublicDepartures = () => {
  const { companyId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cursor, setCursor] = useState(() => { const n = new Date(); return { y: n.getFullYear(), m: n.getMonth() }; });
  const [tab, setTab] = useState('calendar'); // 'calendar' | 'places'
  const [selectedDay, setSelectedDay] = useState(null); // clé jour sélectionnée

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError('');
      try {
        const res = await axios.get(`${API}/public/departures/${companyId}`);
        if (alive) setData(res.data);
      } catch (e) {
        if (alive) setError(e.response?.status === 404 ? 'Entreprise introuvable.' : 'Impossible de charger le calendrier.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [companyId]);

  // Regroupe les départs par jour de départ.
  const byDay = useMemo(() => {
    const map = {};
    for (const dep of (data?.departures || [])) {
      const d = fmtDate(dep.departure_date);
      if (!d) continue;
      (map[dayKey(d)] = map[dayKey(d)] || []).push(dep);
    }
    return map;
  }, [data]);

  // Grille du mois (semaine commençant lundi).
  const weeks = useMemo(() => {
    const first = new Date(cursor.y, cursor.m, 1);
    const startOffset = (first.getDay() + 6) % 7; // 0 = lundi
    const start = new Date(cursor.y, cursor.m, 1 - startOffset);
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
      cells.push(d);
    }
    const rows = [];
    for (let i = 0; i < 42; i += 7) rows.push(cells.slice(i, i + 7));
    // Retirer une dernière semaine entièrement hors du mois.
    return rows.filter((row) => row.some((d) => d.getMonth() === cursor.m) || rows.indexOf(row) < 5);
  }, [cursor]);

  const upcoming = useMemo(() => {
    const now = new Date(); now.setHours(0, 0, 0, 0);
    return (data?.departures || [])
      .map((dep) => ({ dep, d: fmtDate(dep.departure_date) }))
      .filter((x) => x.d && x.d >= now)
      .sort((a, b) => a.d - b.d)
      .slice(0, 12)
      .map((x) => x.dep);
  }, [data]);

  const move = useCallback((delta) => setCursor((c) => {
    const m = c.m + delta;
    return { y: c.y + Math.floor(m / 12), m: ((m % 12) + 12) % 12 };
  }), []);

  const todayKey = dayKey(new Date());

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-5xl mx-auto px-4 py-6 sm:py-10">
        {/* En-tête */}
        <header className="flex items-center gap-3 mb-6">
          {data?.company?.logo_url
            ? <img src={data.company.logo_url} alt="" className="h-12 w-12 rounded object-contain border border-border bg-white" />
            : <div className="h-12 w-12 rounded bg-orange-500/10 text-orange-500 flex items-center justify-center"><CalendarDays size={22} /></div>}
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">{data?.company?.name || 'Calendrier des départs'}</h1>
            <p className="text-sm text-muted-foreground">Calendrier des départs</p>
          </div>
        </header>

        {loading ? (
          <div className="py-20 text-center text-muted-foreground">Chargement…</div>
        ) : error ? (
          <div className="py-20 text-center text-muted-foreground">{error}</div>
        ) : (
          <>
            {/* Onglets */}
            {((data?.relay_points?.length || 0) + (data?.depot_points?.length || 0)) > 0 && (
              <div className="inline-flex rounded-md border border-border overflow-hidden text-sm mb-4">
                <button
                  onClick={() => setTab('calendar')}
                  className={`px-3 py-1.5 inline-flex items-center gap-1.5 ${tab === 'calendar' ? 'bg-orange-500 text-white' : 'bg-background hover:bg-muted'}`}
                >
                  <CalendarDays size={14} /> Calendrier
                </button>
                <button
                  onClick={() => setTab('places')}
                  className={`px-3 py-1.5 border-l border-border inline-flex items-center gap-1.5 ${tab === 'places' ? 'bg-orange-500 text-white' : 'bg-background hover:bg-muted'}`}
                >
                  <Store size={14} /> Points relais & dépôts
                </button>
              </div>
            )}

            {tab === 'places' ? (
              <div className="space-y-8">
                <section>
                  <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-1.5">
                    <Warehouse size={15} /> Points de dépôt
                  </h3>
                  {(data?.depot_points?.length || 0) === 0 ? (
                    <p className="text-sm text-muted-foreground">Aucun point de dépôt renseigné.</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {data.depot_points.map((p, i) => <PlaceCard key={i} place={p} icon={Warehouse} />)}
                    </div>
                  )}
                </section>
                <section>
                  <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-1.5">
                    <Store size={15} /> Points relais
                  </h3>
                  {(data?.relay_points?.length || 0) === 0 ? (
                    <p className="text-sm text-muted-foreground">Aucun point relais renseigné.</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {data.relay_points.map((p, i) => <PlaceCard key={i} place={p} icon={Store} />)}
                    </div>
                  )}
                </section>
              </div>
            ) : (
            <>
            {/* Navigation mois */}
            <div className="flex items-center justify-between mb-3">
              <button onClick={() => move(-1)} className="p-2 rounded-md border border-border hover:bg-muted" aria-label="Mois précédent"><ChevronLeft size={18} /></button>
              <h2 className="text-base sm:text-lg font-semibold">{MONTHS[cursor.m]} {cursor.y}</h2>
              <button onClick={() => move(1)} className="p-2 rounded-md border border-border hover:bg-muted" aria-label="Mois suivant"><ChevronRight size={18} /></button>
            </div>

            {/* Calendrier — grille (toutes tailles, responsive) */}
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="grid grid-cols-7 bg-muted/50 text-[10px] sm:text-xs font-medium text-muted-foreground">
                {WEEKDAYS.map((w) => <div key={w} className="p-1 sm:p-2 text-center">{w}</div>)}
              </div>
              <div className="grid grid-cols-7">
                {weeks.flat().map((d, i) => {
                  const k = dayKey(d);
                  const inMonth = d.getMonth() === cursor.m;
                  const deps = byDay[k] || [];
                  const hasDeps = deps.length > 0;
                  return (
                    <div
                      key={i}
                      onClick={() => hasDeps && setSelectedDay(selectedDay === k ? null : k)}
                      className={`min-h-[52px] sm:min-h-[92px] border-t border-l border-border p-0.5 sm:p-1 ${i % 7 === 6 ? 'border-r' : ''} ${inMonth ? '' : 'bg-muted/30'} ${hasDeps ? 'cursor-pointer hover:bg-muted/50' : ''} ${selectedDay === k ? 'ring-2 ring-inset ring-orange-500' : ''}`}
                    >
                      <div className={`text-[10px] sm:text-[11px] mb-0.5 sm:mb-1 ${k === todayKey ? 'inline-flex items-center justify-center w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-orange-500 text-white' : inMonth ? 'text-foreground' : 'text-muted-foreground/40'}`}>
                        {d.getDate()}
                      </div>
                      {/* Détail (labels) — tablette / desktop */}
                      <div className="hidden sm:block space-y-1">
                        {deps.slice(0, 3).map((dep) => {
                          const meta = statusMeta(dep.status);
                          const featured = isFeatured(dep.status);
                          return (
                            <div key={dep.id} className={`text-[10px] leading-tight px-1 py-0.5 rounded border truncate ${featured ? meta.cls : 'bg-muted text-muted-foreground/50 border-border opacity-70'}`} title={`${dep.name} — ${dep.origin_country} → ${dep.destination_country}`}>
                              {dep.transport_mode === 'maritime' ? '🚢' : '✈️'} {dep.name}
                            </div>
                          );
                        })}
                        {deps.length > 3 && <div className="text-[10px] text-muted-foreground pl-1">+{deps.length - 3}</div>}
                      </div>
                      {/* Indicateur — mobile : icône transport (avion / bateau) */}
                      {hasDeps && (
                        <div className="sm:hidden flex flex-wrap gap-0.5 mt-0.5 text-[11px] leading-none">
                          {deps.slice(0, 4).map((dep) => (
                            <span key={dep.id} className={isFeatured(dep.status) ? '' : 'opacity-40 grayscale'}>{dep.transport_mode === 'maritime' ? '🚢' : '✈️'}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Détail du jour sélectionné (toutes tailles) */}
            {selectedDay && byDay[selectedDay] && (
              <div className="mt-4 border border-orange-500/40 rounded-lg p-3 bg-orange-500/5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-foreground">
                    Départs du {new Date(`${selectedDay}T00:00:00`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </span>
                  <button onClick={() => setSelectedDay(null)} className="text-xs text-muted-foreground hover:text-foreground">Fermer</button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {byDay[selectedDay].map((dep) => <DepartureCard key={dep.id} dep={dep} />)}
                </div>
                {(data?.depot_points?.length || 0) > 0 && (
                  <div className="mt-3 pt-3 border-t border-orange-500/20">
                    <p className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1.5">
                      <Warehouse size={14} /> Où déposer vos colis
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {data.depot_points.map((p, i) => <PlaceCard key={i} place={p} icon={Warehouse} />)}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Astuce : cliquer un jour affiche les départs + le point de dépôt */}
            {!selectedDay && (
              <p className="text-xs text-muted-foreground text-center mt-3">
                Touchez un jour du calendrier pour voir les départs et le point de dépôt des colis.
              </p>
            )}

            {/* Prochains départs */}
            <section className="mt-8">
              <h3 className="text-sm font-semibold text-muted-foreground mb-3">Prochains départs</h3>
              {upcoming.length === 0 ? (
                <p className="text-sm text-muted-foreground">Aucun départ à venir.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {upcoming.map((dep) => <DepartureCard key={dep.id} dep={dep} />)}
                </div>
              )}
            </section>
            </>
            )}
          </>
        )}

        <footer className="mt-10 pt-6 border-t border-border text-center text-xs text-muted-foreground">
          Propulsé par FretManager
        </footer>
      </div>
    </div>
  );
};

export default PublicDepartures;
